import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ColumnDef } from '@tanstack/react-table';
import { DataGrid } from '@/components/DataGrid/DataGrid';
import { useClientDataGrid } from '@/components/DataGrid/useClientDataGrid';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useAlert } from '@/components/ConfirmDialog';
import { downloadExport, ExportSessionExpiredError } from './exportDownload';
import { RegionalMap } from './RegionalMap';
import { waveLabel } from './takataFormat';
import {
  useRegionalDashboard,
  type DealerWithNuts,
  type RegionalProvince,
} from './useRegional';
import { UNKNOWN_CATEGORY_COLOR } from './useTakataDealers';
// The panel grid emits the same DataTables class names as every other grid, so
// it needs the sheet that styles them and the export button.
import '@/styles/stla/page-shell.scss';
import '@/styles/stla/stla_datatables.scss';
import '@/styles/stla/regional.scss';

/**
 * Replaces `Pages/Stellantis/Regional.cshtml` at `/stla/regional`.
 *
 * A NUTS3 choropleth of average appointment wait, with a country filter and a
 * panel that slides up over the map listing the dealers of whichever province is
 * clicked. The KPI tiles recount against the country filter, as they did once
 * `stla_regional.js` had run.
 *
 * Clicking a province also sets the country filter to that province's country —
 * the Razor version did this with a flag (`isProgrammaticFilterChange`) to stop
 * its own change handler from closing the panel it had just opened. Here the
 * click sets both pieces of state in one go, so there is no handler to suppress.
 */

/** Height of the dealer panel, from `regional.scss`; the map frames above it. */
const PANEL_HEIGHT = 300;

export function RegionalPage() {
  const { t } = useTranslation('mystery/dealerlist');
  const alert = useAlert();
  const [searchParams, setSearchParams] = useSearchParams();

  const waveParam = searchParams.get('wave');
  const { data, isLoading, isError } = useRegionalDashboard(waveParam);
  const wave = waveParam ?? data?.selectedWave ?? null;

  /** Country name, as the filter lists them; '' is every country. */
  const [country, setCountry] = useState('');
  const [selectedProvince, setSelectedProvince] = useState<RegionalProvince | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [tableSearch, setTableSearch] = useState('');
  const debouncedTableSearch = useDebouncedValue(tableSearch, 250);
  const [exporting, setExporting] = useState(false);

  /**
   * Country name to ISO code, keyed case-insensitively.
   *
   * `StellantisCountries` builds this map with an OrdinalIgnoreCase comparer, but
   * that comparer does not survive the trip through JSON. The filter lists names
   * as they are displayed ("Italy") while the dealer rows carry the vendor's own
   * spelling ("ITALY"), so a plain lookup matched no dealer at all and every tile
   * read 0 the moment a country was picked.
   */
  const codeByCountry = useMemo(() => {
    const lookup = new Map<string, string>();
    for (const [name, code] of Object.entries(data?.countryCodes ?? {})) {
      lookup.set(name.toUpperCase(), code);
    }
    return lookup;
  }, [data]);

  const countryCode = country ? (codeByCountry.get(country.toUpperCase()) ?? '') : '';

  const dealers = useMemo(() => data?.dealers ?? [], [data]);
  const provinces = useMemo(() => data?.provinces ?? [], [data]);
  const categories = useMemo(() => data?.performanceCategories ?? {}, [data]);

  /**
   * Dealers and provinces under the country filter. The dealer rows carry a
   * country name and the province rows a code, so both are compared as codes.
   */
  const visibleDealers = useMemo(() => {
    if (!countryCode) return dealers;
    return dealers.filter(
      (dealer) => codeByCountry.get((dealer.country ?? '').toUpperCase()) === countryCode,
    );
  }, [dealers, countryCode, codeByCountry]);

  const visibleProvinces = useMemo(() => {
    if (!countryCode) return provinces;
    return provinces.filter((province) => province.country === countryCode);
  }, [provinces, countryCode]);

  /**
   * KPI tiles.
   *
   * The average covers only dealers that booked something: a dealer with no
   * appointment has no wait to average, and counting it as zero would drag every
   * province towards excellent. The dealer and call totals count everyone.
   */
  const kpis = useMemo(() => {
    const booked = visibleDealers.filter((dealer) => dealer.hasAppointment);

    return {
      averageWaitDays:
        booked.length > 0
          ? booked.reduce((sum, d) => sum + (d.averageWaitDays ?? 0), 0) / booked.length
          : 0,
      totalProvinces: visibleProvinces.length,
      totalDealers: visibleDealers.length,
      dealersWithAppointments: booked.length,
      totalCalls: visibleDealers.reduce((sum, d) => sum + (d.calls ?? 0), 0),
    };
  }, [visibleDealers, visibleProvinces]);

  const thresholds = useMemo(() => {
    if (!countryCode || !data) return null;
    const forCountry = data.thresholdsByCountry[countryCode];
    return forCountry && forCountry.length > 0 ? forCountry : null;
  }, [countryCode, data]);

  /** Dealers of the open province — what the panel lists. */
  const provinceDealers = useMemo(() => {
    if (!selectedProvince) return [];
    return dealers.filter((dealer) => dealer.nuts3Code === selectedProvince.nuts3Code);
  }, [dealers, selectedProvince]);

  const handleProvinceClick = useCallback(
    (province: RegionalProvince) => {
      setSelectedProvince(province);
      setCountry(data?.countryNames[province.country] ?? '');
      setTableSearch('');
    },
    [data],
  );

  const handleCountryChange = useCallback((next: string) => {
    setCountry(next);
    // The open province is almost certainly not in the newly chosen country.
    setSelectedProvince(null);
  }, []);

  const mapLabels = useMemo(
    () => ({
      country: t('Dashboard.Filters.Country', 'Country'),
      totalDealers: t('Regional.KPI.TotalDealersCount', 'Dealers'),
      avgWaitTime: t('Regional.KPI.AvgWaitTime', 'Avg Wait'),
      totalCalls: t('Dashboard.Popup.Calls', 'Calls'),
      days: t('Dashboard.KPI.Days', 'days'),
      noAppointments: t('Regional.Map.NoAppointments', 'No appointments scheduled'),
    }),
    [t],
  );

  /**
   * Performance names come from the country's thresholds rather than the
   * category catalogue, which is what the Razor panel did. A category with no
   * threshold row — category 0, the no-appointment case — therefore reads "N/A"
   * here while the dealer dashboards name it.
   */
  const thresholdNames = useMemo(() => {
    const names = new Map<number, string>();
    for (const threshold of selectedProvince
      ? (data?.thresholdsByCountry[selectedProvince.country] ?? [])
      : []) {
      names.set(threshold.categoryIndex, threshold.categoryName);
    }
    return names;
  }, [data, selectedProvince]);

  const columns = useMemo<ColumnDef<DealerWithNuts, unknown>[]>(
    () => [
      { id: 'country', accessorKey: 'country', header: t('Dashboard.Popup.Country', 'Country') },
      { id: 'oic', accessorKey: 'oic', header: t('Oic', 'OIC') },
      { id: 'physOut', accessorKey: 'physOut', header: t('PhysOut', 'PhysOut') },
      {
        id: 'name',
        accessorKey: 'name',
        header: t('Regional.DealersPanel.TableHeaders.Dealer', 'Company Name'),
      },
      { id: 'brand', accessorKey: 'brand', header: t('Dashboard.Popup.Brand', 'Brand') },
      { id: 'carType', accessorKey: 'carType', header: t('Grid.CarType', 'Car Type') },
      { id: 'city', accessorKey: 'city', header: t('Dashboard.Popup.City', 'City') },
      { id: 'address', accessorKey: 'address', header: t('Dashboard.Popup.Address', 'Address') },
      {
        id: 'categoryIndex',
        accessorKey: 'categoryIndex',
        header: t('Dashboard.Filters.Performance', 'Performance'),
        cell: ({ row }) => {
          const index = row.original.categoryIndex;
          const color =
            categories[String(index ?? 0)]?.backgroundColor ?? UNKNOWN_CATEGORY_COLOR;

          return (
            <span className="regional-performance-badge" style={{ backgroundColor: color }}>
              {(index != null && thresholdNames.get(index)) || 'N/A'}
            </span>
          );
        },
      },
      {
        id: 'averageWaitDays',
        accessorKey: 'averageWaitDays',
        header: t('Regional.DealersPanel.TableHeaders.LeadBookingTime', 'Lead Booking Time'),
        cell: ({ row }) =>
          row.original.categoryIndex === 0 || row.original.averageWaitDays == null
            ? '-'
            : `${row.original.averageWaitDays.toFixed(1)} ${t('Dashboard.KPI.Days', 'days')}`,
      },
      { id: 'phone', accessorKey: 'phone', header: t('Dashboard.Popup.Phone', 'Phone') },
      {
        id: 'answerRate',
        accessorKey: 'answerRate',
        header: t('Grid.AnswerRate', 'Answer Rate (%)'),
        cell: ({ row }) => row.original.answerRate?.toFixed(1) ?? '-',
      },
    ],
    [t, categories, thresholdNames],
  );

  const { gridProps } = useClientDataGrid<DealerWithNuts>({
    rows: provinceDealers,
    initialPageSize: 10,
    // Column 8 in the Razor grid, which is the Performance badge.
    initialSorting: [{ id: 'categoryIndex', desc: false }],
    search: debouncedTableSearch,
    searchFields: [
      'country',
      'oic',
      'physOut',
      'name',
      'brand',
      'carType',
      'city',
      'address',
      'phone',
    ],
  });

  const handleExport = useCallback(async () => {
    if (exporting || !selectedProvince) return;
    setExporting(true);
    try {
      const params = new URLSearchParams({ nuts3: selectedProvince.nuts3Code });
      if (wave) params.set('wave', wave);
      await downloadExport(
        `/api/stla/regional/export?${params.toString()}`,
        `Regional_Dealers_${selectedProvince.nuts3Code}.xlsx`,
      );
    } catch (error) {
      if (error instanceof ExportSessionExpiredError) {
        window.location.href = '/auth-login';
        return;
      }
      // `t` here is the mystery/dealerlist namespace; the title comes from shared.
      void alert({
        variant: 'danger',
        title: t('shared:Message.Error', 'Error'),
        message: t('Grid.ExportError', 'Could not export data. Please try again.'),
      });
    } finally {
      setExporting(false);
    }
  }, [exporting, selectedProvince, wave, t, alert]);

  if (isError) {
    return (
      <div className="alert alert-danger m-3" role="alert">
        {t('Regional.LoadError', 'Could not load the regional view. Please try again.')}
      </div>
    );
  }

  return (
    // `regional-view` is what this screen's stylesheet is scoped to; see
    // styles/stla/page-shell.scss for why it has to be.
    <div className="regional-page regional-view">
      <div className="regional-container">
        <div className={`regional-sidebar${sidebarCollapsed ? ' collapsed' : ''}`}>
          <div className="regional-sidebar-header">
            <h2>{t('Regional.Title', 'Regional view')}</h2>
            <p>{t('Regional.Subtitle', 'Appointment wait by province')}</p>
          </div>

          <div className="regional-kpi-section">
            <h3>{t('Regional.KPI.KeyMetrics', 'Key metrics')}</h3>
            <div className="regional-kpi-grid">
              <div className="regional-kpi-card">
                <div className="regional-kpi-label">
                  {t('Dashboard.KPI.AverageWaitTime', 'Average wait time')}
                </div>
                <div className="regional-kpi-value">
                  {kpis.averageWaitDays.toFixed(1)} {t('Dashboard.KPI.Days', 'days')}
                </div>
              </div>
              <div className="regional-kpi-card">
                <div className="regional-kpi-label">
                  {t('Regional.KPI.TotalProvinces', 'Provinces')}
                </div>
                <div className="regional-kpi-value">{kpis.totalProvinces}</div>
              </div>
              <div className="regional-kpi-card">
                <div className="regional-kpi-label">
                  {t('Dashboard.KPI.TotalDealers', 'Dealers')}
                </div>
                <div className="regional-kpi-value">{kpis.totalDealers}</div>
              </div>
              <div className="regional-kpi-card">
                <div className="regional-kpi-label">
                  {t('Dashboard.KPI.Appointments', 'Appointments')}
                </div>
                <div className="regional-kpi-value">{kpis.dealersWithAppointments}</div>
              </div>
              <div className="regional-kpi-card">
                <div className="regional-kpi-label">{t('Dashboard.KPI.TotalCalls', 'Total calls')}</div>
                <div className="regional-kpi-value">{kpis.totalCalls}</div>
              </div>
            </div>

            {/*
             * All five tiles recount from the wave, so they read 0 until it lands.
             * The map overlay already announces the loading state, so this one is
             * decorative.
             */}
            {isLoading && (
              <div className="regional-loading-overlay" aria-hidden="true">
                <div className="spinner" />
              </div>
            )}
          </div>

          <div className={`regional-thresholds-section${thresholds ? ' visible' : ''}`}>
            <h3>{t('Dashboard.Thresholds.Title', 'Thresholds')}</h3>
            <div>
              {(thresholds ?? []).map((threshold) => (
                <div className="regional-threshold-item" key={threshold.categoryIndex}>
                  <span
                    className="regional-threshold-badge"
                    style={{
                      backgroundColor:
                        categories[String(threshold.categoryIndex)]?.backgroundColor ??
                        UNKNOWN_CATEGORY_COLOR,
                    }}
                  >
                    {threshold.categoryName || `Category ${threshold.categoryIndex}`}
                  </span>
                  <span className="regional-threshold-value">{threshold.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="regional-filters-section">
            <h3>{t('Dashboard.Filters.Title', 'Filters')}</h3>

            <div className="regional-filter-group">
              <label htmlFor="waveFilter">Wave</label>
              <select
                id="waveFilter"
                value={wave ?? ''}
                onChange={(event) => setSearchParams({ wave: event.target.value })}
              >
                {(data?.availableWaves ?? []).map((w) => (
                  <option key={w} value={w}>
                    {waveLabel(w)}
                  </option>
                ))}
              </select>
            </div>

            <div className="regional-filter-group">
              <label htmlFor="countryFilter">{t('Dashboard.Filters.Country', 'Country')}</label>
              <select
                id="countryFilter"
                value={country}
                onChange={(event) => handleCountryChange(event.target.value)}
              >
                <option value="">{t('Dashboard.Filters.AllCountries', 'All countries')}</option>
                {(data?.countries ?? []).map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="regional-map-container">
          <div className="map-controls">
            <button
              type="button"
              className="btn-map-control"
              onClick={() => setSidebarCollapsed((collapsed) => !collapsed)}
              title={t('Dashboard.Map.ToggleSidebar', 'Toggle sidebar')}
              aria-label={t('Dashboard.Map.SidebarAriaLabel', 'Toggle the filters sidebar')}
              aria-expanded={!sidebarCollapsed}
            >
              <span aria-hidden="true">📋</span>
              <span>{t('Dashboard.Map.Sidebar', 'Sidebar')}</span>
            </button>
          </div>

          {isLoading ? (
            <div className="regional-loading-overlay" role="status" aria-live="polite">
              <div className="spinner" aria-hidden="true" />
              <span className="visually-hidden">
                {t('Regional.Map.Loading', 'Loading provinces…')}
              </span>
            </div>
          ) : (
            <RegionalMap
              provinces={provinces}
              dealers={dealers}
              categories={categories}
              thresholdsByCountry={data?.thresholdsByCountry ?? {}}
              selectedCountry={countryCode}
              onProvinceClick={handleProvinceClick}
              labels={mapLabels}
              bottomPadding={selectedProvince ? PANEL_HEIGHT : 0}
              resizeToken={`${sidebarCollapsed}-${selectedProvince?.nuts3Code ?? ''}`}
            />
          )}

          <div className={`regional-dealers-panel${selectedProvince ? ' visible' : ''}`}>
            <div className="regional-dealers-panel-header">
              <h3 className="regional-dealers-panel-title">
                {t('Regional.DealersPanel.Title', 'Dealers')}{' '}
                {selectedProvince ? `${selectedProvince.nuts3Name} (${provinceDealers.length})` : ''}
              </h3>

              <div className="regional-dealers-panel-actions">
                <input
                  type="search"
                  className="regional-dealers-search"
                  value={tableSearch}
                  onChange={(event) => setTableSearch(event.target.value)}
                  placeholder={t('Grid.Search', 'Search:')}
                  aria-label={t('Grid.Search', 'Search:')}
                />
                <button
                  type="button"
                  className="btn-export-excel"
                  onClick={handleExport}
                  disabled={exporting || provinceDealers.length === 0}
                >
                  {exporting
                    ? t('Grid.Exporting', 'Exporting…')
                    : t('Grid.ExportExcel', 'Export Excel')}
                </button>
                <button
                  type="button"
                  className="btn-close-panel"
                  onClick={() => setSelectedProvince(null)}
                >
                  {t('Regional.DealersPanel.Close', 'Close')}
                </button>
              </div>
            </div>

            <div className="regional-dealers-panel-content">
              <DataGrid<DealerWithNuts>
                {...gridProps}
                columns={columns}
                tableClassName="regional-dealers-table"
                getRowId={(row, index) => `${row.physOut}-${row.brand}-${index}`}
                pageSizeOptions={[10, 25, 50]}
                emptyMessage={t(
                  'Regional.DealersPanel.NoData',
                  'No dealers found in this province',
                )}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
